// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * The model-vote A/B: geodata's stored classical detections judged twice, as the decision takes
 * them today and with one trained model's vote, both held against what people and OSM say is
 * there (geodata's ModelVoteAb). Read-only. Asking for another model or vote strength recomputes
 * it, and nothing reaches players until a vote is configured in the service.
 */

/** One arm of the comparison for one kind. Every number is geodata's; the page only shows them. */
export type ModelVoteArm = {
  /** Detections that clear the serving floor. */
  clears: number;
  clearsReal: number;
  clearsFalse: number;
  hiddenReal: number;
  hiddenFalse: number;
  /** Null when no judged detection clears the floor. */
  judgedPrecision: number | null;
  /** Null when no detection is known to be real. */
  judgedRecall: number | null;
};

export type ModelVoteKind = {
  kind: string;
  candidates: number;
  agrees: number;
  contradicts: number;
  inconclusive: number;
  notAsked: number;
  /** Detections OSM, GPS or a person vouches for. */
  knownReal: number;
  /** Detections a person rejected. */
  knownFalse: number;
  without: ModelVoteArm;
  with: ModelVoteArm;
};

export type ModelVoteCourse = {
  courseId: string;
  courseName: string | null;
  candidates: number;
  agrees: number;
  contradicts: number;
  clearsWithout: number;
  clearsWith: number;
};

/** A model version that has detection runs to compare with. */
export type ModelVoteChoice = { version: string; nickname: string | null; courses: number };

/** The vote production runs with, as configured; null when there is none. */
export type ModelVoteInForce = { version: string; delta: number; coverFraction: number; kinds: string[] };

/** One arm on held-out courses: what clears the serving floor, and how it scores against OSM. */
export type ModelVoteHoldoutArm = {
  clears: number;
  matched: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
};

export type ModelVoteHoldoutKind = {
  kind: string;
  /** Every OSM feature on the held-out ground, found or not. */
  mapped: number;
  detections: number;
  agrees: number;
  contradicts: number;
  without: ModelVoteHoldoutArm;
  with: ModelVoteHoldoutArm;
};

/** The vote scored on the newest evaluate run's held-out courses — the unbiased half of the A/B. */
export type ModelVoteHoldout = {
  evaluatedAt: string | null;
  provider: string | null;
  courses: number;
  /** False for a run older than stored confidences, which cannot be re-scored. */
  scored: boolean;
  byKind: ModelVoteHoldoutKind[];
};

export type ModelVoteAb = {
  /** Null when no model has run on any course — there is nothing to compare. */
  version: string | null;
  nickname: string | null;
  delta: number;
  coverFraction: number;
  servedFloor: number;
  inForce: ModelVoteInForce | null;
  versions: ModelVoteChoice[];
  coursesAvailable: number;
  coursesJudged: number;
  byKind: ModelVoteKind[];
  byCourse: ModelVoteCourse[];
  /** Null when the model has no evaluation on held-out courses. */
  holdout: ModelVoteHoldout | null;
};

type Row = Record<string, unknown>;

/** A field by its camelCase name, or the PascalCase one a differently configured serializer writes. */
const pick = (row: Row, key: string): unknown => row[key] ?? row[key.charAt(0).toUpperCase() + key.slice(1)];

const num = (row: Row, key: string): number => {
  const value = pick(row, key);
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const numOrNull = (row: Row, key: string): number | null => {
  const value = pick(row, key);
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const str = (row: Row, key: string): string | null => {
  const value = pick(row, key);
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const rows = (row: Row, key: string): Row[] => {
  const value = pick(row, key);
  return Array.isArray(value) ? value.filter((v): v is Row => typeof v === 'object' && v !== null) : [];
};

const mapArm = (raw: unknown): ModelVoteArm => {
  const row = (typeof raw === 'object' && raw !== null ? raw : {}) as Row;
  return {
    clears: num(row, 'clears'),
    clearsReal: num(row, 'clearsReal'),
    clearsFalse: num(row, 'clearsFalse'),
    hiddenReal: num(row, 'hiddenReal'),
    hiddenFalse: num(row, 'hiddenFalse'),
    judgedPrecision: numOrNull(row, 'judgedPrecision'),
    judgedRecall: numOrNull(row, 'judgedRecall'),
  };
};

const mapHoldoutArm = (raw: unknown): ModelVoteHoldoutArm => {
  const row = (typeof raw === 'object' && raw !== null ? raw : {}) as Row;
  return {
    clears: num(row, 'clears'),
    matched: num(row, 'matched'),
    precision: numOrNull(row, 'precision'),
    recall: numOrNull(row, 'recall'),
    f1: numOrNull(row, 'f1'),
  };
};

const mapHoldout = (raw: unknown): ModelVoteHoldout | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as Row;
  return {
    evaluatedAt: str(row, 'evaluatedAt'),
    provider: str(row, 'provider'),
    courses: num(row, 'courses'),
    scored: pick(row, 'scored') === true,
    byKind: rows(row, 'byKind')
      .map((k) => ({
        kind: str(k, 'kind') ?? '',
        mapped: num(k, 'mapped'),
        detections: num(k, 'detections'),
        agrees: num(k, 'agrees'),
        contradicts: num(k, 'contradicts'),
        without: mapHoldoutArm(pick(k, 'without')),
        with: mapHoldoutArm(pick(k, 'with')),
      }))
      .filter((k) => k.kind.length > 0),
  };
};

/** Maps the BFF's `/api/geodata/ml/model-vote` body. Exported for direct unit testing. */
export const mapModelVoteAb = (body: unknown): ModelVoteAb | null => {
  if (typeof body !== 'object' || body === null) return null;
  const row = body as Row;
  const inForce = pick(row, 'inForce');
  const inForceRow = typeof inForce === 'object' && inForce !== null ? (inForce as Row) : null;
  const inForceVersion = inForceRow ? str(inForceRow, 'version') : null;
  return {
    version: str(row, 'version'),
    nickname: str(row, 'nickname'),
    delta: num(row, 'delta'),
    coverFraction: num(row, 'coverFraction'),
    servedFloor: num(row, 'servedFloor'),
    inForce:
      inForceRow && inForceVersion
        ? {
            version: inForceVersion,
            delta: num(inForceRow, 'delta'),
            coverFraction: num(inForceRow, 'coverFraction'),
            kinds: (() => {
              const kinds = pick(inForceRow, 'kinds');
              return Array.isArray(kinds) ? kinds.filter((k): k is string => typeof k === 'string') : [];
            })(),
          }
        : null,
    versions: rows(row, 'versions')
      .map((v) => ({ version: str(v, 'version'), nickname: str(v, 'nickname'), courses: num(v, 'courses') }))
      .filter((v): v is ModelVoteChoice => v.version !== null),
    coursesAvailable: num(row, 'coursesAvailable'),
    coursesJudged: num(row, 'coursesJudged'),
    byKind: rows(row, 'byKind')
      .map((k) => ({
        kind: str(k, 'kind') ?? '',
        candidates: num(k, 'candidates'),
        agrees: num(k, 'agrees'),
        contradicts: num(k, 'contradicts'),
        inconclusive: num(k, 'inconclusive'),
        notAsked: num(k, 'notAsked'),
        knownReal: num(k, 'real'),
        knownFalse: num(k, 'false'),
        without: mapArm(pick(k, 'without')),
        with: mapArm(pick(k, 'with')),
      }))
      .filter((k) => k.kind.length > 0),
    byCourse: rows(row, 'byCourse')
      .map((c) => ({
        courseId: str(c, 'courseId') ?? '',
        courseName: str(c, 'courseName'),
        candidates: num(c, 'candidates'),
        agrees: num(c, 'agrees'),
        contradicts: num(c, 'contradicts'),
        clearsWithout: num(c, 'clearsWithout'),
        clearsWith: num(c, 'clearsWith'),
      }))
      .filter((c) => c.courseId.length > 0),
    holdout: mapHoldout(pick(row, 'holdout')),
  };
};

export type ModelVoteState = {
  ab: ModelVoteAb | null;
  loading: boolean;
  error: 'forbidden' | 'unavailable' | 'failed' | null;
};

/**
 * Reads the comparison for `version` at vote strength `delta`; null for either means geodata's
 * default — the vote in force, else the model that ran most recently.
 */
export function useMlModelVote(
  apiBaseUrl: string,
  token: string | null | undefined,
  enabled: boolean,
  version: string | null,
  delta: number | null
): ModelVoteState {
  const [ab, setAb] = useState<ModelVoteAb | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ModelVoteState['error']>(null);

  useEffect(() => {
    if (!enabled || !token) {
      setAb(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: string[] = [];
    if (version) params.push(`version=${encodeURIComponent(version)}`);
    if (delta !== null) params.push(`delta=${delta}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/model-vote${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!response.ok) {
          setError(response.status === 403 ? 'forbidden' : response.status === 404 ? 'unavailable' : 'failed');
          return;
        }
        const body = mapModelVoteAb(await response.json());
        if (!cancelled) setAb(body);
      } catch {
        if (!cancelled) setError('failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, enabled, token, version, delta]);

  return { ab, loading, error };
}
